import { useEffect, useState, type FormEvent } from 'react';
import type { MultiFactorResolver, TotpSecret } from 'firebase/auth';
import QRCode from 'qrcode';
import { finishEnroll, firebaseReady, isEmulator, logout, resolveSecondFactor, signInEmulator, signInGoogle, startEnroll } from '../lib/firebase';
import { GoogleLogo } from '../components/ui';

const codeOk = (c: string) => /^\d{6}$/.test(c);

/** 6 桁の入力（認証アプリの数字） */
export function CodeField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="field"><label htmlFor="totp">認証アプリの 6 桁</label>
      <input id="totp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} placeholder="000000" style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center' }} autoFocus /></div>
  );
}

/** ログイン（仕様書「事務所 Web の入口」：Google でログインし、2 段階目の 6 桁を入れる。登録したスタッフだけが入れる） */
export function Login({ error }: { error?: string }) {
  const [err, setErr] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'first' | 'code'>('first');
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [code, setCode] = useState('');
  // 名簿にない・入れなかったときは、最初の画面に戻す
  useEffect(() => { if (error) { setStep('first'); setCode(''); } }, [error]);
  const google = async () => {
    setErr('');
    try { const r = await signInGoogle(); if (r) { setResolver(r); setStep('code'); } } catch { setErr('ログインできませんでした'); }
  };
  const dev = (e: FormEvent) => { e.preventDefault(); setErr(''); setStep('code'); };
  const second = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    if (!codeOk(code)) { setErr('6 桁の数字を入れてください'); return; }
    try { if (isEmulator) signInEmulator(email); else await resolveSecondFactor(resolver!, code); }
    catch { setErr('数字が違います。認証アプリの今の 6 桁を入れてください'); setCode(''); }
  };
  const shown = err || error;
  return (
    <div className="login">
      <div className="box">
        <b>とらぽん</b>
        <h1>事務所</h1>
        {step === 'first' ? (<>
          <p className="muted" style={{ margin: 0 }}>登録されたスタッフの Google アカウントでログインしてください。</p>
          {firebaseReady && !isEmulator && <button className="gsi" onClick={google}><GoogleLogo /><span>Google でログイン</span></button>}
          {isEmulator && (
            <form onSubmit={dev} style={{ display: 'grid', gap: 8 }}>
              <div className="field"><label htmlFor="dev-email">開発用：メールアドレスでログイン</label>
                <input id="dev-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="narita@example.co.jp" required /></div>
              <button className="btn pri">ログイン</button>
              <small className="muted">Firebase のエミュレーターに Google でログインしたことにします（手元だけ）</small>
            </form>
          )}
          {!firebaseReady && <p className="err">Firebase の設定（.env.local）がありません</p>}
        </>) : (
          <form onSubmit={second} style={{ display: 'grid', gap: 10 }}>
            <p className="muted" style={{ margin: 0 }}>2 段階目：スマホの認証アプリ（Google Authenticator など）に出ている 6 桁を入れてください。</p>
            <CodeField value={code} onChange={setCode} />
            <button className="btn pri" disabled={!codeOk(code)}>確認する</button>
            {isEmulator && <small className="muted">手元では 6 桁の数字なら通ります</small>}
            <button type="button" className="btn" onClick={() => { setStep('first'); setCode(''); }}>戻る</button>
          </form>
        )}
        {shown && <p className="err" role="alert">{shown}</p>}
      </div>
    </div>
  );
}

/** 2 段階目の登録（初めてのログイン）：QR コードを認証アプリで読み、6 桁を入れる */
export function Enroll({ email }: { email: string }) {
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    startEnroll().then(async (s) => { setSecret(s); setQr(await QRCode.toDataURL(s.generateQrCodeUrl(email, 'とらぽん 事務所'), { margin: 1, width: 220 })); })
      .catch(() => setErr('登録を始められませんでした。ログインし直してください'));
  }, [email]);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    if (!secret || !codeOk(code)) { setErr('6 桁の数字を入れてください'); return; }
    try { await finishEnroll(secret, code); } catch { setErr('数字が違います。認証アプリの今の 6 桁を入れてください'); setCode(''); }
  };
  return (
    <div className="login">
      <div className="box">
        <b>とらぽん</b>
        <h1>2 段階目の登録</h1>
        <p className="muted" style={{ margin: 0 }}>事務所の画面は、Google のログインのあとに、スマホの認証アプリの 6 桁が必要です。はじめに 1 回だけ登録します。</p>
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, display: 'grid', gap: 4 }}>
          <li>スマホに認証アプリ（Google Authenticator など）を入れる</li>
          <li>アプリで下の QR コードを読む</li>
          <li>アプリに出た 6 桁を入れる</li>
        </ol>
        {qr ? <img src={qr} alt="認証アプリで読む QR コード" width={220} height={220} style={{ justifySelf: 'center' }} /> : !err && <p className="muted">準備しています…</p>}
        {secret && <small className="muted" style={{ overflowWrap: 'anywhere' }}>読めないときは、このキーを入れる：{secret.secretKey}</small>}
        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <CodeField value={code} onChange={setCode} />
          <button className="btn pri" disabled={!codeOk(code)}>登録する</button>
        </form>
        <button className="btn" onClick={() => void logout()}>やめる</button>
        {err && <p className="err" role="alert">{err}</p>}
      </div>
    </div>
  );
}
